import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import Header from './Header';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  rejected: 'bg-red-100 text-red-800'
};

const Invoice = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadOrder = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (orderSnap.exists()) {
        setOrder({
          id: orderSnap.id,
          ...orderSnap.data()
        });
      } else {
        setError('Order not found');
      }
    } catch (err) {
      console.error('Error loading order:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // Handle Firebase Timestamp
    let dateObj;
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      dateObj = new Date(date);
    }
    
    return dateObj.toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center text-gray-500 text-xl">Loading invoice...</div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center text-red-500 text-xl">{error || 'Order not found'}</div>
        </div>
      </div>
    );
  }

  // Only allow viewing invoice for shipped orders
  if (order.status !== 'shipped') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-red-500 text-xl font-semibold mb-2">Invoice Not Available</div>
            <div className="text-gray-600">
              Invoice is only available for shipped orders. Current status: {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
            </div>
            <button 
              onClick={() => navigate(-1)} 
              className="mt-4 flex items-center text-primary font-semibold hover:underline mx-auto"
            >
              <ArrowBackIcon className="mr-2 !w-5 !h-5" />
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      <div className="print:hidden">
        <Header />
      </div>
      <div className="print:hidden pt-20">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center text-primary font-semibold hover:underline"
            >
              <ArrowBackIcon className="mr-2 !w-5 !h-5" />
              Back
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-900 transition-colors"
            >
              <PrintIcon className="!w-5 !h-5" />
              Print Invoice
            </button>
          </div>
        </div>
      </div>

      {/* Invoice Content - Print Optimized */}
      <div className="mx-auto px-4 py-8 print:py-0 print:px-0 print:m-0 print:w-full print:h-full print:block print:flex print:justify-center">
        <div className="bg-white p-8 print:p-12 shadow-lg print:shadow-none print:m-0 flex flex-col" style={{ 
          width: '210mm', 
          minHeight: '297mm', 
          maxWidth: '210mm',
          margin: '0 auto'
        }}>
          <div className="flex-grow">
          {/* Header */}
          <div className="border-b-4 border-primary pb-6 mb-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-4xl font-bold text-primary mb-2">INVOICE</h1>
                {order.invoiceNumber && (
                  <p className="text-lg font-semibold text-gray-700">Invoice Number: {order.invoiceNumber}</p>
                )}
              </div>
              <div className="text-right">
                <div className="font-bold text-lg mb-1">MMK Industries Sdn Bhd</div>
                <div className="text-sm text-gray-600">201801014475 (1276491-D)</div>
                <div className="text-sm text-gray-600">No 2, Medan Bendahara 1</div>
                <div className="text-sm text-gray-600">31650 Ipoh, Perak, Malaysia</div>
                <div className="text-sm text-gray-600 mt-2">Tel: +605-253 3729</div>
                <div className="text-sm text-gray-600">Email: industriesmmk@gmail.com</div>
              </div>
            </div>
          </div>

          {/* Order and Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 bg-gray-50 p-6 rounded-lg">
            <div>
              <h3 className="text-lg font-bold text-primary mb-3">Order Information</h3>
              <p className="mb-2"><strong>ID:</strong> {order.orderId || order.id}</p>
              <p className="mb-2"><strong>Date:</strong> {formatDate(order.dateTime || order.createdAt)}</p>
              <p className="mb-2">
                <strong>Status:</strong> 
                <span className={`ml-2 px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                  {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                </span>
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-primary mb-3">Bill To</h3>
              <p className="font-semibold mb-2">{order.purchaserName}</p>
              <p className="mb-2">{order.purchaserPhoneNumber}</p>
              {typeof order.purchaserAddress === 'object' ? (
                <>
                  <p>{order.purchaserAddress.line1}</p>
                  {order.purchaserAddress.line2 && <p>{order.purchaserAddress.line2}</p>}
                  <p>{order.purchaserAddress.city}</p>
                  <p>{order.purchaserAddress.postcode} {order.purchaserAddress.state}</p>
                </>
              ) : (
                <p>{order.purchaserAddress}</p>
              )}
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="text-left p-3 border border-gray-300">Item</th>
                  <th className="text-center p-3 border border-gray-300">Quantity</th>
                  <th className="text-right p-3 border border-gray-300">Unit Price</th>
                  <th className="text-right p-3 border border-gray-300">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.products?.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-300">
                    <td className="p-3 border border-gray-300">{item.productName || item.name}</td>
                    <td className="p-3 border border-gray-300 text-center">{item.quantity}</td>
                    <td className="p-3 border border-gray-300 text-right">{formatCurrency(item.amount || 0)}</td>
                    <td className="p-3 border border-gray-300 text-right font-semibold">
                      {formatCurrency(item.totalAmount || item.amount * item.quantity || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mb-8">
            <div className="flex justify-end">
              <div className="w-full md:w-96">
                <div className="flex justify-between py-2">
                  <span className="text-gray-700">Subtotal:</span>
                  <span className="font-medium">{formatCurrency(order.amount || 0)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-700">Shipping Fee:</span>
                  <span className="font-medium">
                    {order.shippingFees === 0 ? 'FREE' : formatCurrency(order.shippingFees || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-4 border-t-2 border-primary mt-2">
                  <span className="text-xl font-bold text-primary">Total Amount:</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(order.totalAmount || order.total || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-300 pt-6 mt-auto text-center text-gray-600 text-sm">
            <p className="mb-2">Computer generated invoice. No signature required.</p>
            <p>For inquiries, please contact us at industriesmmk@gmail.com or +605-253 3729</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media screen {
          /* Scale down A4 on smaller screens */
          @media (max-width: 768px) {
            [style*="width: 210mm"] {
              width: 95vw !important;
              max-width: 95vw !important;
              min-height: auto !important;
            }
          }
        }
        @media print {
          body {
            background: white;
            margin: 0;
            padding: 0;
          }
          /* Hide App wrapper header and footer */
          header,
          footer {
            display: none !important;
            visibility: hidden !important;
          }
          /* Hide elements with print:hidden class */
          .print\\:hidden {
            display: none !important;
          }
          /* Hide main wrapper padding and ensure full height */
          main {
            padding-top: 0 !important;
            margin: 0 !important;
          }
          /* Ensure only invoice content is visible */
          body > div:first-child {
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Remove all margins from invoice wrapper and container */
          div[style*="width: 210mm"] {
            margin: 0 !important;
          }
          /* Remove margins from root-level wrapper divs only (not internal invoice content) */
          body > div[class*="min-h-screen"],
          body > div[class*="min-h-screen"] > div[class*="mx-auto"] {
            margin: 0 !important;
          }
          /* Specific invoice wrapper margin removal */
          .print\\:justify-center {
            justify-content: center !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:py-0 {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          .print\\:px-0 {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .print\\:m-0 {
            margin: 0 !important;
          }
          .print\\:w-full {
            width: 100% !important;
          }
          .print\\:h-full {
            height: 100% !important;
          }
          .print\\:block {
            display: block !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          @page {
            size: A4;
            margin: 0 !important;
          }
          /* Remove any default browser print margins */
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            height: 100% !important;
          }
          .print\\:p-12 {
            padding: 48px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Invoice;

